import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, alpha } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import ApprovalStatusChip from 'components/chip/ApprovalStatusChip';
import TaskStatusChip from 'components/chip/TaskStatusChip';
import TicketCategoryChip from 'components/chip/TicketCategoryChip';
import TicketPriorityChip from 'components/chip/TicketPriorityChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  useQueryInitialFilters,
} from 'components/console';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import {
  ApprovalStatus,
  TaskStatus,
  TicketListResult,
  listTicket,
  updateTaskAssignee,
  updateTaskStatus,
} from 'stores/TicketStoreAdapter';
import { TicketTypeSummaryResult, listTicketType } from 'stores/TicketTypeStoreAdapter';
import { formatDateOrDash } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import TicketCreateDialog from './components/TicketCreateDialog';
import TicketCreateSplitButton from './components/TicketCreateSplitButton';
import TicketDetailView, { useTicketDetail } from './components/TicketDetailView';
import { openTicketInNewTab } from './ticketNavigation';

const LIST_LIMIT = 200;

const STATUS_LANES: { status: TaskStatus; label: string; color: string }[] = [
  { status: 'NOT_STARTED', label: '未着手', color: '#757575' },
  { status: 'IN_PROGRESS', label: '着手中', color: '#ed6c02' },
  { status: 'DONE', label: '完了', color: '#2e7d32' },
  { status: 'CLOSED', label: 'クローズ', color: '#455a64' },
];

type ViewMode = 'status' | 'assignee';

/**
 * 看板ボード（タスク専用・議題10-3）。
 * ステータス別4レーン ⇄ 担当者別レーンを切替。カードD&Dで API7111（ステータス）/ API7112（担当者）。
 * 楽観更新（失敗時は元に戻す）。
 */
const TicketBoard: React.FC = () => {
  const navigate = useNavigate();
  const { users, userSession } = useAuth();
  const drawer = useTicketDetail();

  const [tickets, setTickets] = useState<TicketListResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [progress, setProgress] = useState(true);
  const [priorities, setPriorities] = useState<MasterTypeResult[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('status');
  const [activeParams, setActiveParams] = useState<Record<string, unknown>>({});
  const [dragId, setDragId] = useState<string | null>(null);
  // D&D中にカードが乗っているレーン（ドロップ先の枠を色付けして視認性を高める）
  const [dragOverLane, setDragOverLane] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTypeId, setCreateTypeId] = useState<string | undefined>(undefined);
  const [types, setTypes] = useState<TicketTypeSummaryResult[]>([]);
  const [parents, setParents] = useState<TicketListResult[]>([]);
  const activeTaskTypes = useMemo(
    () => types.filter((t) => t.isActive && t.ticketCategory === 'TASK'),
    [types],
  );

  const openCreate = (typeId: string) => {
    setCreateTypeId(typeId);
    setCreateOpen(true);
  };

  useEffect(() => {
    listTicketType({ activeOnly: true })
      .then(setTypes)
      .catch(() => setTypes([]));
    getMasterType('priority')
      .then(setPriorities)
      .catch(() => setPriorities([]));
    // 親チケット候補（タスク・親のみ）を取得して親チケット検索の選択肢にする
    listTicket({ ticketCategory: 'TASK', onlyParent: true })
      .then((res) => setParents(res.results))
      .catch(() => setParents([]));
  }, []);

  // IN 検索対象キー（同じキーで複数チップ→配列に集約）
  const MULTI_KEYS = ['typeIds', 'statuses', 'priorities', 'assigneeUserIds'];

  const fetchTickets = (params: Record<string, unknown>) => {
    setProgress(true);
    const payload: Record<string, unknown> = { ...params, ticketCategory: 'TASK' };
    MULTI_KEYS.forEach((k) => {
      if (payload[k] === undefined || payload[k] === null || payload[k] === '') return;
      if (!Array.isArray(payload[k])) payload[k] = [payload[k]];
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listTicket(payload as any)
      .then((res) => {
        setTickets(res.results);
        setTotalCount(res.totalCount);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('チケットの取得に失敗しました');
      });
  };

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'keyword', name: 'キーワード', type: 'text' as const, color: '#B0BEC5' },
      { key: 'managementId', name: '管理ID', type: 'text' as const, color: '#78C1F3' },
      {
        key: 'assigneeUserId',
        name: '担当者',
        type: 'user-select-single' as const,
        color: '#F7C8E0',
        users,
      },
      {
        key: 'typeIds',
        name: '種別',
        type: 'select' as const,
        color: '#B39DDB',
        values: types.map((t) => ({ key: t.typeId, name: t.name })),
      },
      {
        key: 'priorities',
        name: '優先度',
        type: 'select' as const,
        color: '#FFCC80',
        values: priorities.map((p) => ({ key: p.typeCode, name: p.typeLabel })),
      },
      {
        key: 'parentTicketId',
        name: '親チケット',
        type: 'ticket-select-single' as const,
        color: '#A1887F',
        tickets: parents,
      },
      {
        key: 'scope',
        name: 'スコープ',
        type: 'select' as const,
        color: '#80CBC4',
        values: [
          { key: 'GLOBAL', name: 'グローバル' },
          { key: 'PRIVATE', name: 'プライベート' },
        ],
      },
    ],
    [users, priorities, types, parents],
  );

  const handleSearch = (filters: Filter[]) => {
    const params: Record<string, unknown> = {};
    filters.forEach((f) => {
      if (MULTI_KEYS.includes(f.key)) {
        const arr = (params[f.key] as string[] | undefined) ?? [];
        arr.push(f.value.key);
        params[f.key] = arr;
      } else {
        params[f.key] = f.value.key;
      }
    });
    setActiveParams(params);
    fetchTickets(params);
  };

  // URLクエリ初期条件（ダッシュボード等からの遷移用。self 解決）
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions, {
    selfUserId: userSession.userId,
    selfKeys: ['assigneeUserId', 'creatorUserId', 'approverUserId'],
  });

  useEffect(() => {
    setActiveParams(queryParams);
    fetchTickets(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const priorityName = (code: string | null) =>
    code ? priorities.find((p) => p.typeCode === code)?.typeLabel : undefined;

  // 担当者別レーン（検索結果に含まれる担当者のみ）
  const assigneeLanes = useMemo(() => {
    const map = new Map<string, { userId: string; userName: string }>();
    tickets.forEach((t) => {
      if (!map.has(t.assigneeUserId)) {
        map.set(t.assigneeUserId, { userId: t.assigneeUserId, userName: t.assigneeUserName });
      }
    });
    return Array.from(map.values());
  }, [tickets]);

  // 楽観更新: ステータス変更
  const moveStatus = (ticketId: string, status: TaskStatus) => {
    const prev = tickets;
    const target = tickets.find((t) => t.ticketId === ticketId);
    if (!target || target.status === status) return;
    setTickets((ts) => ts.map((t) => (t.ticketId === ticketId ? { ...t, status } : t)));
    updateTaskStatus({ ticketId, status }).catch(() => {
      errorDefault('ステータスの更新に失敗しました');
      setTickets(prev);
    });
  };

  // 楽観更新: 担当者変更
  const moveAssignee = (ticketId: string, assigneeUserId: string, assigneeUserName: string) => {
    const prev = tickets;
    const target = tickets.find((t) => t.ticketId === ticketId);
    if (!target || target.assigneeUserId === assigneeUserId) return;
    setTickets((ts) =>
      ts.map((t) => (t.ticketId === ticketId ? { ...t, assigneeUserId, assigneeUserName } : t)),
    );
    updateTaskAssignee({ ticketId, assigneeUserId }).catch(() => {
      errorDefault('担当者の変更に失敗しました');
      setTickets(prev);
    });
  };

  const renderCard = (t: TicketListResult) => (
    <Box
      key={t.ticketId}
      draggable
      onDragStart={() => setDragId(t.ticketId)}
      onDragEnd={() => {
        setDragId(null);
        setDragOverLane(null);
      }}
      onClick={() => drawer.load(t.ticketId)}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.25,
        mb: 1,
        bgcolor: 'background.paper',
        cursor: 'grab',
        opacity: dragId === t.ticketId ? 0.5 : 1,
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} color="primary.main">
          {t.managementId}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {t.priority && <TicketPriorityChip code={t.priority} label={priorityName(t.priority)} />}
      </Stack>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
        {t.title}
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {t.assigneeUserName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDateOrDash(t.scheduledEndDate)}
        </Typography>
      </Stack>
    </Box>
  );

  const Lane: React.FC<{
    laneKey: string;
    title: string;
    color: string;
    items: TicketListResult[];
    onDropCard: (ticketId: string) => void;
  }> = ({ laneKey, title, color, items, onDropCard }) => {
    const isOver = dragId !== null && dragOverLane === laneKey;
    return (
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          if (dragOverLane !== laneKey) setDragOverLane(laneKey);
        }}
        onDragLeave={(e) => {
          // 子要素間の移動で発火するleaveは無視し、レーン外に出た時のみクリア
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverLane((prev) => (prev === laneKey ? null : prev));
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (dragId) onDropCard(dragId);
          setDragId(null);
          setDragOverLane(null);
        }}
        sx={{
          width: 260,
          flexShrink: 0,
          bgcolor: isOver ? alpha(color, 0.16) : 'action.hover',
          border: 2,
          borderStyle: isOver ? 'dashed' : 'solid',
          borderColor: isOver ? color : 'transparent',
          borderRadius: 2,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100%',
          transition: 'background-color 0.15s, border-color 0.15s',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1, px: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({items.length})
          </Typography>
        </Stack>
        <Box sx={{ flex: 1, overflow: 'auto' }}>{items.map(renderCard)}</Box>
      </Box>
    );
  };

  const overLimit = totalCount > LIST_LIMIT;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ConsoleHeader
        title="看板ボード"
        count={totalCount}
        description="タスクをステータス/担当者別にドラッグして操作します"
        backLabel="チケット管理"
        onBack={() => navigate('/ticket')}
        onRefresh={() => fetchTickets(activeParams)}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={(_, v: ViewMode | null) => v && setViewMode(v)}
            >
              <ToggleButton value="status" sx={{ textTransform: 'none', px: 1.5 }}>
                ステータス別
              </ToggleButton>
              <ToggleButton value="assignee" sx={{ textTransform: 'none', px: 1.5 }}>
                担当者別
              </ToggleButton>
            </ToggleButtonGroup>
            <TicketCreateSplitButton activeTypes={activeTaskTypes} onCreate={openCreate} />
          </Stack>
        }
      />

      <Box sx={{ mb: 1 }}>
        <ConsoleSearchFilter
          conditions={searchConditions}
          initialFilters={initialFilters}
          onFiltersChange={handleSearch}
          conditionKey="ticket-board"
          userProfileKey="ticket-board-search"
        />
      </Box>

      {overLimit && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          他 {totalCount - tickets.length} 件は表示されていません。検索条件で絞り込んでください。
        </Typography>
      )}

      <Stack direction="row" spacing={1.5} sx={{ flex: 1, overflow: 'auto', pb: 1 }}>
        {viewMode === 'status'
          ? STATUS_LANES.map((lane) => (
              <Lane
                key={lane.status}
                laneKey={`status:${lane.status}`}
                title={lane.label}
                color={lane.color}
                items={tickets.filter((t) => t.status === lane.status)}
                onDropCard={(id) => moveStatus(id, lane.status)}
              />
            ))
          : assigneeLanes.map((a) => (
              <Lane
                key={a.userId}
                laneKey={`assignee:${a.userId}`}
                title={a.userName}
                color="#1976d2"
                items={tickets.filter((t) => t.assigneeUserId === a.userId)}
                onDropCard={(id) => moveAssignee(id, a.userId, a.userName)}
              />
            ))}
        {viewMode === 'assignee' && assigneeLanes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            表示するタスクがありません
          </Typography>
        )}
      </Stack>

      <ConsoleDetailDrawer
        open={drawer.detail !== null}
        onClose={drawer.clear}
        title={drawer.detail?.managementId ?? ''}
        badges={
          drawer.detail && (
            <>
              <TicketCategoryChip ticketCategory={drawer.detail.ticketCategory} />
              {drawer.detail.ticketCategory === 'TASK' ? (
                <TaskStatusChip status={drawer.detail.status as TaskStatus} />
              ) : (
                <ApprovalStatusChip status={drawer.detail.status as ApprovalStatus} />
              )}
            </>
          )
        }
        width="min(80vw, 1100px)"
      >
        {drawer.detail && (
          <Box sx={{ height: '100%', mx: -2.5, mb: -2.5 }}>
            <TicketDetailView
              detail={drawer.detail}
              priorities={priorities}
              onReload={() => {
                if (drawer.detail) drawer.load(drawer.detail.ticketId);
                fetchTickets(activeParams);
              }}
              onOpenTicket={openTicketInNewTab}
            />
          </Box>
        )}
      </ConsoleDetailDrawer>

      <TicketCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => fetchTickets(activeParams)}
        activeTypes={activeTaskTypes}
        priorities={priorities}
        initialTypeId={createTypeId}
      />

      <Circular open={progress} />
    </Box>
  );
};

export default TicketBoard;
